import type { IconName } from '../../components/icons';
import type { BadgeTone, Status } from '../../components/ui';
import i18n from '../../i18n';
import type { Messages } from '../../i18n/fr';
import type { ActivityEntry } from '../../lib/api';

/**
 * The activity log in plain words (R01, A01-03): "**Lead Follow-up** replied
 * to **Maya Haddad**", never the raw event key (`workspace.reactivated`).
 * Entries of one automation run share an `executionId` and become one row,
 * "**Maya Haddad** — **Lead Follow-up** a répondu au commentaire, a envoyé un
 * message puis s’est terminée", with one chip per step. Names come from the
 * automation, contact and tag an entry points at; an unknown event falls back
 * to the server's own message.
 */

export type ActivityTone = 'accent' | 'positive' | 'caution' | 'neutral';

export interface ActivityPart {
  text: string;
  strong?: boolean;
}

export interface ActivityLine {
  icon: IconName;
  tone: ActivityTone;
  parts: ActivityPart[];
}

export interface ActivityNames {
  automation?: string;
  contact?: string;
  tag?: string;
}

/** A step of a run, shown under its sentence: a status for how it ended, a badge for what it did. */
export type RunChip = { kind: 'status'; status: Status } | { kind: 'badge'; label: string; tone: BadgeTone };

type LineKey = keyof Messages['activity']['line'];
type StepKey = keyof Messages['activity']['step'];
type ChipKey = keyof Messages['activity']['chip'];
const t = i18n.t.bind(i18n);
const strong = (text: string): ActivityPart => ({ text, strong: true });
const plain = (text: string): ActivityPart => ({ text });

/** A catalogue sentence with its `{{names}}` swapped for parts, so names stay bold in any word order. */
export function fillParts(template: string, values: Record<string, ActivityPart>): ActivityPart[] {
  return template
    .split(/(\{\{\w+\}\})/)
    .filter(Boolean)
    .map((piece) => {
      const key = /^\{\{(\w+)\}\}$/.exec(piece)?.[1];
      return key && values[key] ? values[key] : plain(piece);
    });
}

/** `Tag "Interested" added` → `Interested`, for entries whose tag is gone. */
function tagFromMessage(message: string): string | undefined {
  return /"([^"]+)"/.exec(message)?.[1];
}

export function describeActivity(entry: ActivityEntry, names: ActivityNames): ActivityLine {
  const tagName = names.tag ?? tagFromMessage(entry.message);
  const values = {
    automation: strong(names.automation ?? t('activity.someAutomation')),
    contact: names.contact ? strong(names.contact) : plain(t('activity.aContact')),
    tag: tagName ? strong(tagName) : plain(t('activity.aTag')),
  };
  const line = (icon: IconName, tone: ActivityTone, key: LineKey, extra: Record<string, ActivityPart> = {}): ActivityLine => ({
    icon,
    tone,
    parts: fillParts(t(`activity.line.${key}`), { ...values, ...extra }),
  });

  switch (entry.type) {
    case 'automation.started':
      return line('zap', 'accent', 'started');
    case 'automation.completed':
      return line('checkCircle', 'positive', 'completed');
    case 'automation.failed':
      return line('alert', 'caution', 'failed');
    case 'automation.canceled':
      return line('x', 'neutral', 'canceled');
    case 'automation.limit_blocked':
      return line('alert', 'caution', 'limitBlocked');
    case 'automation.retrigger_ignored':
      return line('pause', 'neutral', 'retriggerIgnored');
    case 'automation.takeover_suppressed':
      return line('pause', 'neutral', 'takeoverSuppressed');
    case 'automation.workspace_suspended':
      return line('pause', 'caution', 'workspaceSuspended');
    case 'message.sent':
      return entry.actorType === 'automation' ? line('send', 'accent', 'repliedBy') : line('send', 'accent', 'sent');
    case 'message.reacted':
      return line('heart', 'accent', 'reacted');
    case 'message.react_failed':
      return line('alert', 'caution', 'reactFailed');
    case 'comment.replied':
      return line('comment', 'accent', 'commentReplied');
    case 'email.sent':
      return line('mail', 'accent', 'emailSent');
    case 'tag.added':
      return names.contact ? line('tag', 'caution', 'tagged') : line('tag', 'caution', 'tagAdded');
    case 'tag.removed':
      return line('tagOff', 'neutral', 'tagRemoved');
    case 'conversation.agent_replied':
      return line('reply', 'accent', 'agentReplied');
    case 'conversation.closed':
      return line('checkCircle', 'neutral', 'closed');
    case 'conversation.reopened':
      return line('inbox', 'neutral', 'reopened');
    case 'conversation.automations_paused':
      return line('pause', 'neutral', 'paused');
    case 'conversation.automations_resumed':
      return line('play', 'positive', 'resumed');
    case 'workspace.suspended':
      return line('power', 'caution', 'workspace', { state: strong(t('activity.suspended')) });
    case 'workspace.reactivated':
      return line('power', 'positive', 'workspace', { state: strong(t('activity.reactivated')) });
    case 'contact.erased': {
      // D-185: the entry names the member who erased, never the person erased.
      const actorName = entry.metadata['actorName'];
      const member = typeof actorName === 'string' && actorName ? strong(actorName) : plain(t('activity.aTeammate'));
      return line('trash', 'neutral', 'contactErased', { member });
    }
    default:
      return { icon: 'info', tone: entry.status === 'failure' ? 'caution' : 'neutral', parts: [plain(entry.message)] };
  }
}

// --- one row per run --------------------------------------------------------------------

const badge = (key: ChipKey, tone: BadgeTone = 'pri') => (): RunChip => ({ kind: 'badge', label: t(`activity.chip.${key}`), tone });
const status = (value: Status) => (): RunChip => ({ kind: 'status', status: value });

/** What each event of a run adds to its sentence and its chips; other events stay out of the row. */
const RUN_STEPS: Record<string, { step: StepKey; chip: () => RunChip }> = {
  'comment.replied': { step: 'commentReplied', chip: badge('commentReplied') },
  'message.sent': { step: 'sent', chip: badge('sent') },
  'message.reacted': { step: 'reacted', chip: badge('reacted') },
  'email.sent': { step: 'emailSent', chip: badge('emailSent') },
  'tag.added': { step: 'tagAdded', chip: badge('tagAdded') },
  'tag.removed': { step: 'tagRemoved', chip: badge('tagRemoved') },
  'automation.completed': { step: 'completed', chip: status('completed') },
  'automation.failed': { step: 'failed', chip: status('failed') },
  'automation.canceled': { step: 'canceled', chip: status('cancelled') },
  'automation.limit_blocked': { step: 'limitBlocked', chip: badge('limitBlocked', 'warn') },
};

/** "a, b puis c" / "a, b, then c". */
function joinSteps(steps: string[]): string {
  if (steps.length < 2) return steps[0] ?? '';
  return `${steps.slice(0, -1).join(', ')}${t('activity.then')}${steps.at(-1)}`;
}

export interface RunLine {
  line: ActivityLine;
  chips: RunChip[];
}

/**
 * One run's entries (any order) as one sentence and its chips. Its start is
 * left out once anything else happened; alone, it reads "a démarré" (still
 * running). Returns null when none of its events has a step.
 */
export function describeRun(
  entries: ActivityEntry[],
  names: { automation?: string; contact?: string },
  tagOf: (entry: ActivityEntry) => string | undefined,
): RunLine | null {
  const ordered = [...entries].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const known = ordered.filter((e) => RUN_STEPS[e.type]);
  const steps: string[] = [];
  const chips: RunChip[] = [];
  const seen = new Set<string>();
  for (const entry of known) {
    const { step, chip } = RUN_STEPS[entry.type]!;
    const phrase = t(`activity.step.${step}`, { tag: tagOf(entry) ?? t('activity.aTag') });
    if (steps.at(-1) !== phrase) steps.push(phrase);
    const made = chip();
    const chipKey = made.kind === 'status' ? made.status : made.label;
    if (!seen.has(chipKey)) {
      seen.add(chipKey);
      chips.push(made);
    }
  }
  if (steps.length === 0) {
    if (!ordered.some((e) => e.type === 'automation.started')) return null;
    steps.push(t('activity.step.started'));
  }

  const failed = known.some((e) => e.type === 'automation.failed' || e.type === 'automation.limit_blocked');
  const finished = known.some((e) => e.type === 'automation.completed');
  const values = {
    contact: strong(names.contact ?? t('activity.aContact')),
    automation: strong(names.automation ?? t('activity.someAutomationInline')),
    steps: plain(joinSteps(steps)),
  };
  const parts = fillParts(t(names.contact ? 'activity.run' : 'activity.runNoContact'), values);
  // Without a contact the automation opens the sentence, so it takes a capital.
  if (!names.contact && !names.automation) parts[0] = strong(t('activity.someAutomation'));
  return {
    line: { icon: failed ? 'alert' : finished ? 'checkCircle' : 'zap', tone: failed ? 'caution' : finished ? 'positive' : 'accent', parts },
    chips,
  };
}

export interface ActivityGroup {
  key: string;
  /** Newest first, as the log returns them. */
  entries: ActivityEntry[];
}

/**
 * The log (newest first) in rows: a run's entries together under the newest,
 * every other entry on its own. A run start outside any run is noise beside
 * its outcome and stays out (as before the redesign).
 */
export function groupActivity(entries: ActivityEntry[], limit: number): ActivityGroup[] {
  const groups = new Map<string, ActivityEntry[]>();
  for (const entry of entries) {
    if (!entry.executionId && entry.type === 'automation.started') continue;
    const key = entry.executionId ? `run:${entry.executionId}` : entry.id;
    const group = groups.get(key);
    if (group) group.push(entry);
    else if (groups.size < limit) groups.set(key, [entry]);
  }
  return [...groups.entries()].map(([key, items]) => ({ key, entries: items }));
}
